import * as React from 'react';
import { Alert } from 'antd';
import {
  first, isEmpty, map,
} from 'lodash';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { Notice } from '@affiliates/AspireUI';
import {
  OfferDetails,
  ShopifyScopesMissing,
} from '@affiliates/components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { CircleDollarIcon, UserGroupIcon, WalletIcon } from '@revfluence/fresh-icons/solid/esm';
import { faGlobe } from '@fortawesome/free-solid-svg-icons';
import { MainMetric, Tag, WidgetContainer } from '@frontend/app/refresh-components';
import { Skeleton } from '@frontend/shadcn/components/ui/skeleton';
import { EmailComposeButton, MembersWizard } from '@affiliates/containers';
import {
  ISTAComposerMemberFieldIds,
  MEMBER_FIRST_NAME,
  OfferDetailsQueriesStatus,
  STA_FIELD_NAME,
  useOfferDetailsQueries,
} from '@affiliates/hooks';
import { OFFER_SOURCE, OFFER_STATUS, UserInfoInput } from '@affiliates/types/globalTypes';
import { DateRangeOptions, IDateRangeSettings } from '@frontend/app/components';
import { Button, Modal, Typography } from '@revfluence/fresh';
import { useOfferDetailsContext } from '@frontend/context/OfferDetailsContext';
import moment from 'moment';
import { BoxArchiveIcon, CircleExclamationIcon } from '@revfluence/fresh-icons/regular';
import { useClientFeatures } from '@frontend/context/ClientFeatureContext';
import { DataTable } from '@frontend/app/refresh-components/DataTableV2';
import { Tooltip, TooltipContent, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import { TooltipArrow, TooltipBody, TooltipHeader, TooltipItem, TooltipItemKey, TooltipItemValue, TooltipTitle } from '@frontend/applications/Social/components/tooltip';
import { MemberConversionDrawer } from '../components/MemberConversionDrawer';
import styles from '../components/OfferDetails/OfferDetails.scss';
import { MigrationModelContent } from '../components/MigrationModelContent';
import { OfferLogDrawer } from '../components/OfferLogDrawer';
import ShopifyStoreDisconnectionAlert from '../components/ShopifyStoreDisconnectionAlert/ShopifyStoreDisconnectionAlert';
import AlertBar from './Dashboard/LandingPageNavigation';
import { TOfferMember } from '../components/MemberTable/types';
import OfferDashboardTabs from './OfferDashboardTabs';
import { RefreshMemberTable } from '../components/MemberTable/RefreshMemberTable';
import { RefreshManageOffer } from '../components/ManageOffer/RefreshManageOffer';

const dummyColumns = [
  { id: 'offerName', header: 'Name', accessorKey: 'offerName' },
  { id: 'programId', header: 'Connected Project', accessorKey: 'programId' },
  { id: 'url', header: 'URL', accessorKey: 'url' },
  { id: 'status', header: 'Status', accessorKey: 'status' },
  { id: 'members', header: 'Members', accessorKey: 'members' },
  { id: 'clicks', header: 'Clicks', accessorKey: 'clicks' },
  { id: 'conversions', header: 'Conversions', accessorKey: 'conversions' },
  { id: 'salesAmount', header: 'Sales Amt', accessorKey: 'salesAmount' },
  { id: 'payoutEarned', header: 'Commissions Earned', accessorKey: 'payoutEarned' },
  { id: 'payoutMade', header: 'Payout', accessorKey: 'payoutMade' },
  { id: 'avgSale', header: 'Avg. Sale', accessorKey: 'avgSale' },
  { id: 'createdDate', header: 'Created On', accessorKey: 'createdDate' },
];
const { useState, useEffect } = React;
interface IProps {
  baseUri: string;
  dateRangeSettings: IDateRangeSettings;
  shopifyAppId: string;
  staComposerMemberFieldIds: ISTAComposerMemberFieldIds;
  profile: UserInfoInput;
  brandName?: string;
  onDeletedMembersChange?: (members: readonly TOfferMember[]) => void;
  isDeleteAction: boolean;
  setIsDeleteAction: (value: boolean) => void;
  selectedPayoutIds: number[];
  setSelectedPayoutIds: (ids: number[]) => void;
  setIsMemberTableFilterOpen: (open: boolean) => void;
}
interface StatCardProps {
  title: string;
  bgColor: string;
  widgetIcon: React.ReactNode;
  value: number;
  metricType: 'number' | 'currency' | 'count';
  trendIncrease?: number;
  subHeading?: string;
  primaryPopoverText?: string;
  secondaryPopoverText?: string;
  isLoading?: boolean;
  tooltip?: string;
  secondaryValue?: {
    value: number;
    symbol?: string;
  };
}
function StatCard({
  title,
  bgColor,
  widgetIcon,
  value,
  metricType,
  trendIncrease,
  subHeading,
  primaryPopoverText,
  secondaryPopoverText,
  isLoading,
  tooltip,
  secondaryValue,
}: StatCardProps) {
  const loadingContent = (
    <div className="flex flex-col gap-2">
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-full" />
    </div>
  );
  const mainContent = (
    <MainMetric
      value={value}
      size="small"
      icon={null}
      metricType={metricType}
      trend={trendIncrease}
      subHeading={subHeading}
      className="p-2 pt-0 -mt-2"
      popoverTextPrimary={primaryPopoverText}
      popoverTextSecondary={secondaryPopoverText}
      secondaryValue={secondaryValue}
    />
  );
  return (
    <WidgetContainer widgetTitle={title} bgColor={bgColor} widgetIcon={widgetIcon} tooltip={tooltip}>
      {isLoading ? loadingContent : mainContent}
    </WidgetContainer>
  );
}

interface IParams {
  offerId: string;
}
interface StateType {
  isUpdated: boolean;
}

function formatArchivedMessage(archivedDate: string | null, archivedUserName: string | null): string {
  if (!archivedDate || !archivedUserName) return '';

  return `This offer was archived on ${moment(archivedDate).format('MM/DD/YY')} by ${archivedUserName}. All offer insights are still available but you can no longer add new members.`;
}

export const RefreshOfferDetailsPage: React.FC<Readonly<IProps>> = (props) => {
  const {
    baseUri, dateRangeSettings, shopifyAppId, staComposerMemberFieldIds, profile, brandName, onDeletedMembersChange,
    isDeleteAction,
    setIsDeleteAction,
    selectedPayoutIds,
    setSelectedPayoutIds,
    setIsMemberTableFilterOpen,
  } = props;

  const { Text } = Typography;
  const location = useLocation<StateType | undefined>();
  const {
    migrateToGraphQL,
    enableMultipleShopify: isEnabledMultipleShopify,
    archiveOffer,
    refreshUi,
  } = useClientFeatures();
  const { offerId: offerIdStr } = useParams<IParams>();
  const offerId = Number(offerIdStr);
  const { isManageDeepLinksAllowed, linkForPromo, shopifyLandingPage } = useClientFeatures();
  const {
    setOffer,
    isOfferLogDrawerVisible,
    setIsOfferLogDrawerVisible,
    showMigrationModal,
    setShowMigrationModal,
    isOldOffer,
    setOfferFormValue,
  } = useOfferDetailsContext();
  const queries = useOfferDetailsQueries(offerId, dateRangeSettings, isEnabledMultipleShopify);
  const history = useHistory();
  const [isNotifyModalOpen, openNotifyMemberModal] = useState(false);
  const state = location.state;
  const closeNotifyModal = () => {
    if (state) {
      state.isUpdated = false;
    }
    openNotifyMemberModal(false);
  };

  useEffect(() => {
    setOffer(null);
    setOfferFormValue(null);
    setIsOfferLogDrawerVisible(false);
  }, [setIsOfferLogDrawerVisible, setOffer, setOfferFormValue]);

  const hasEffectRun = React.useRef(false);
  useEffect(() => {
    if (queries.status === OfferDetailsQueriesStatus.Ready) {
      setOffer(queries.offerDetails);
      dateRangeSettings.setEarliestDate(queries.offerDetails.createdDate);
    }
    if (!hasEffectRun.current && queries.status === OfferDetailsQueriesStatus.Ready) {
      const affiliateOfferLinkGenerated = first(queries.offerDetails.links)?.affiliateOfferLinkGenerated;
      hasEffectRun.current = true;
      if (affiliateOfferLinkGenerated && state && state?.isUpdated) {
        openNotifyMemberModal(true);
      }
    }
  }, [queries, dateRangeSettings, openNotifyMemberModal, state, setOffer]);
  useEffect(() => {
    if (
      queries.status === OfferDetailsQueriesStatus.Ready
      && queries.offerDetails?.archivedDate !== null
      && archiveOffer
    ) {
      Modal.warning({
        title: 'Archive Offer',
        content: formatArchivedMessage(
          queries.offerDetails?.archivedDate,
          queries.offerDetails?.archivedUser?.name,
        ),
        icon: <CircleExclamationIcon />,
        autoFocusButton: null,
        okText: 'Continue',
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.status, archiveOffer]);

  useEffect(() => {
    if (queries.status === OfferDetailsQueriesStatus.Ready) {
      onDeletedMembersChange?.(queries.deletedMembers);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queries.status, isDeleteAction, onDeletedMembersChange, setIsDeleteAction]);

  const getOfferDetailsPageContents = () => {
    switch (queries.status) {
      case OfferDetailsQueriesStatus.Loading:
        return (
          <>
            <div className="grid grid-cols-1 gap-4 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 p-4">
              <StatCard
                title="Members"
                bgColor="bg-purple-5"
                widgetIcon={<UserGroupIcon className="text-grey-0" fontSize={20} />}
                value={0}
                metricType="count"
                subHeading="0 with Conversions"
                tooltip="Members are the total number of users who have signed up for the offer."
                isLoading
              />
              <StatCard
                title="Conversions"
                bgColor="bg-blue-5"
                // widgetIcon={<GlobeStandIcon className="text-white" fontSize={20} />}
                widgetIcon={<FontAwesomeIcon icon={faGlobe} className="text-white" fontSize={20} />}
                value={0}
                metricType="number"
                tooltip="Conversions are the total number of users who have signed up for the offer."
                isLoading
              />
              <StatCard
                title="Total Sales"
                bgColor="bg-teal-5"
                widgetIcon={<CircleDollarIcon className="text-white" fontSize={20} />}
                value={0}
                metricType="currency"
                tooltip="Total Sales are the total number of sales for the offer."
                isLoading
              />
              <StatCard
                title="Commissions Earned"
                bgColor="bg-lime-5"
                widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                value={0}
                metricType="currency"
                tooltip="Commissions Earned are the total number of commissions earned for the offer."
                isLoading
              />
              <StatCard
                title="Payouts"
                bgColor="bg-yellow-5"
                widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                value={0}
                metricType="currency"
                tooltip="Payouts are the total number of payouts completed for the offer."
                isLoading
              />
            </div>
            <DataTable
              columns={dummyColumns}
              data={Array(8).fill({})}
              isLoading
            />
          </>);
      case OfferDetailsQueriesStatus.Failed:
        return <Notice type="error" message={queries.error.message} />;
      default:
        let offerStatus = null;
        let source = OFFER_SOURCE.SHOPIFY;
        let isMultipleShopifyOffer: boolean = false;
        if (!isEmpty(queries.offerDetails.promos)) {
          source = OFFER_SOURCE.SHOPIFY;
          isMultipleShopifyOffer = first(queries.offerDetails.promos)?.multiShopifyEnabled;
          if (queries.missingShopifyCredentials) {
            offerStatus = OFFER_STATUS.PAUSED;
          } else {
            offerStatus = first(queries.offerDetails.promos).status;
          }
        } else if (!isEmpty(queries.offerDetails.links) && !queries.offerDetails.isPromoLink) {
          source = OFFER_SOURCE.TUNE;
          offerStatus = first(queries.offerDetails.links).status;
        }
        let includedMemberIds = queries.memberTable.selectedAffiliateIds;
        if (isEmpty(includedMemberIds)) {
          includedMemberIds = map(queries.offerMembers, (m) => m.memberId);
        }
        const disableCompose = queries.memberTable.disableCompose
          || staComposerMemberFieldIds[MEMBER_FIRST_NAME] < 1
          || staComposerMemberFieldIds[STA_FIELD_NAME] < 1;
        const emailComposeButton = (
          <EmailComposeButton
            disableCompose={disableCompose}
            includeMemberIds={includedMemberIds}
            offer={queries.offerDetails}
            staComposerMemberFieldIds={staComposerMemberFieldIds}
            source={source}
            refreshSTADashboard
          />
        );

        const emailUpdateComposeButton = (
          <EmailComposeButton
            disableCompose={disableCompose}
            includeMemberIds={includedMemberIds}
            offer={queries.offerDetails}
            staComposerMemberFieldIds={staComposerMemberFieldIds}
            source={source}
            profile={profile}
            isSourceEditOffer
            brandName={brandName}
          />
        );

        const includeFailedMembersWizard = !isEmpty(queries.failedMembers.members)
          && !(source === OFFER_SOURCE.SHOPIFY && queries.missingShopifyCredentials);
        const data = queries.offerStats;
        return (
          <>
            <div className="flex gap-2 px-4 pt-4 flex-wrap">
              <Tooltip>
                <TooltipTrigger asChild>
                  <div>
                    <Tag
                      label={dateRangeSettings.selectedOption === DateRangeOptions.CUSTOM
                        ? `${dateRangeSettings.dateRange?.startDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${dateRangeSettings.dateRange?.endDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                        : dateRangeSettings.selectedOption}
                      bgColor="bg-grey-2"
                      textColor="text-grey-6"
                      onRemove={() => {
                        dateRangeSettings.setEarliestDate(null);
                        dateRangeSettings.onChange(DateRangeOptions.ALL_TIME);
                      }}
                      isRemovable={dateRangeSettings.selectedOption !== DateRangeOptions.ALL_TIME}
                    />
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                  <TooltipHeader>
                    <TooltipTitle>Date Range Filter</TooltipTitle>
                  </TooltipHeader>
                  <TooltipBody>
                    <TooltipItem>
                      <TooltipItemKey>Selected Date Range</TooltipItemKey>
                      <TooltipItemValue>
                        {dateRangeSettings.selectedOption === DateRangeOptions.CUSTOM
                          ? `${dateRangeSettings.dateRange?.startDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })} - ${dateRangeSettings.dateRange?.endDate?.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`
                          : dateRangeSettings.selectedOption}
                      </TooltipItemValue>
                    </TooltipItem>
                  </TooltipBody>
                  <TooltipArrow />
                </TooltipContent>
              </Tooltip>
              {selectedPayoutIds.length > 0 && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div>
                      <Tag
                        label={(() => {
                          const selectedPayouts = queries.offerDetails?.payouts?.filter((payout) => selectedPayoutIds.includes(payout.id)) || [];
                          if (selectedPayouts.length === 0) return 'No payouts selected';
                          const firstPayout = selectedPayouts[0];
                          const remainingCount = selectedPayouts.length - 1;

                          return `${firstPayout.label}${firstPayout.flatPayout && firstPayout.percentPayout
                            ? ` ($${firstPayout.flatPayout} + ${firstPayout.percentPayout}%)`
                            : firstPayout.flatPayout
                              ? ` ($${firstPayout.flatPayout})`
                              : firstPayout.percentPayout
                                ? ` (${firstPayout.percentPayout}%)`
                                : ''}${remainingCount > 0 ? ` +${remainingCount}` : ''}`;
                        })()}
                        bgColor="bg-grey-2"
                        textColor="text-grey-6"
                        onRemove={() => setSelectedPayoutIds([])}
                        isRemovable
                        className="cursor-pointer"
                        onClick={() => setIsMemberTableFilterOpen(true)}
                      />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent side="top" className="bg-grey-0 text-grey-6 shadow-lg border border-gray-200 rounded-md px-3 py-2 font-medium max-w-[200px]">
                    <TooltipHeader>
                      <TooltipTitle>Payout Filter</TooltipTitle>
                    </TooltipHeader>
                    <TooltipBody>
                      <TooltipItem>
                        <TooltipItemKey>Selected Payouts</TooltipItemKey>
                        <TooltipItemValue>
                          {queries.offerDetails?.payouts
                            ?.filter((payout) => selectedPayoutIds.includes(payout.id))
                            .map((payout) => (
                              <div key={payout.id} className="mb-1">
                                {payout.label}
                                {payout.flatPayout && payout.percentPayout
                                  ? ` ($${payout.flatPayout} + ${payout.percentPayout}%)`
                                  : payout.flatPayout
                                    ? ` ($${payout.flatPayout})`
                                    : payout.percentPayout
                                      ? ` (${payout.percentPayout}%)`
                                      : ''}
                              </div>
                            ))}
                        </TooltipItemValue>
                      </TooltipItem>
                    </TooltipBody>
                    <TooltipArrow />
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
            {shopifyLandingPage && linkForPromo && queries.offerDetails.isLandingPageEnabled && (
              <AlertBar baseUri={baseUri} offerId={offerId} landingPageUrl={queries.offerDetails.landingPageUrl} isOfferExpired={queries.offerDetails.expired || queries.offerDetails?.archivedDate} />
            )}
            {queries.offerDetails.archivedDate && (
              <div className={styles.archiveOfferAlert}>
                <Alert
                  message={formatArchivedMessage(
                    queries.offerDetails?.archivedDate,
                    queries.offerDetails?.archivedUser?.name,
                  )}
                  type="warning"
                  icon={<BoxArchiveIcon />}
                  showIcon
                />
              </div>
            )}
            {migrateToGraphQL && isOldOffer && (
              <Alert
                message="Upgrade Your Offer: To continue editing or adding members to your offer, please upgrade to the latest sales tracking capabilities. Click 'Upgrade Now' to proceed. "
                type="info"
                showIcon
                action={(
                  <Button size="small" type="ghost" onClick={() => setShowMigrationModal(true)}>
                    Upgrade Now
                  </Button>
                )}
                style={{ marginBottom: '8px' }}
              />
            )}
            <div className="p-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 xs:grid-cols-1 sm:grid-cols-2 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
                <StatCard
                  title="Members"
                  bgColor="bg-purple-5"
                  widgetIcon={<UserGroupIcon className="text-grey-0" fontSize={20} />}
                  value={queries.offerMembers.length}
                  metricType="count"
                  subHeading={`${data.memberConversionCount} with Conversions`}
                  tooltip="Members are the total number of users who have signed up for the offer."
                />
                <StatCard
                  title="Conversions"
                  bgColor="bg-blue-5"
                  // widgetIcon={<GlobeStandIcon className="text-white" fontSize={20} />}
                  widgetIcon={<FontAwesomeIcon icon={faGlobe} className="text-white" fontSize={20} />}
                  value={data.conversions}
                  metricType="number"
                  subHeading={`${data.clicks || 0} clicks`}
                  primaryPopoverText={data.conversions?.toLocaleString('en-US')}
                  tooltip="Conversions are the total number of users who have signed up for the offer."
                />
                <StatCard
                  title="Total Sales"
                  bgColor="bg-teal-5"
                  widgetIcon={<CircleDollarIcon className="text-white" fontSize={20} />}
                  value={data.sales}
                  metricType="currency"
                  subHeading={`${(data.avgSale || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} Sales per Member`}
                  primaryPopoverText={`$${data.sales?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  tooltip="Total Sales are the total number of sales for the offer."
                  secondaryValue={{ value: queries.offerStats?.salesBase, symbol: queries.offerStats?.currencies?.[0] }}
                />
                <StatCard
                  title="Commissions Earned"
                  bgColor="bg-lime-5"
                  widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                  value={data.payoutEarned}
                  metricType="currency"
                  subHeading={`$${queries.offerMembers.length > 0 ? (data.payoutEarned / queries.offerMembers.length).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '0.00'} Avg per Member`}
                  primaryPopoverText={`$${data.payoutEarned?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                  tooltip="Commissions Earned are the total number of commissions earned for the offer."
                  secondaryValue={{ value: queries.offerStats?.payoutEarnedBase, symbol: queries.offerStats?.currencies?.[0] }}
                />
                <StatCard
                  title="Payments Due"
                  bgColor="bg-yellow-5"
                  widgetIcon={<WalletIcon className="text-white" fontSize={20} />}
                  value={Number(((data.payoutEarned || 0) - (data.payoutMade || 0)).toFixed(2))}
                  metricType="currency"
                  subHeading={`${data.membersWithPayoutDue} Members`}
                  primaryPopoverText={`$${data.payoutEarned - data.payoutMade} Payments Due`}
                  tooltip="Payouts are the total number of payouts completed for the offer."
                />
              </div>
              {source === OFFER_SOURCE.SHOPIFY && (
                queries.missingShopifyCredentials ? (
                  <ShopifyScopesMissing
                    onClick={() => {
                      history.push({
                        ...location,
                        pathname: `/settings/${encodeURIComponent(shopifyAppId)}`,
                      });
                    }}
                  />
                ) : (
                  <ShopifyStoreDisconnectionAlert refreshUi={refreshUi} />

                )
              )}
              {!isEmpty(queries.multipleShopifyOfferStats) && (<OfferDashboardTabs storeWiseConversions={queries.multipleShopifyOfferStats} />)}
              <RefreshMemberTable
                buttonActions={queries.memberTable.buttonActions}
                emailComposeButton={emailComposeButton}
                fixFailedMembers={queries.failedMembers.showFailedMembers}
                members={queries.offerMembers}
                missingShopifyCredentials={source === OFFER_SOURCE.SHOPIFY && queries.missingShopifyCredentials}
                offer={queries.offerDetails}
                currencies={queries.offerStats?.currencies}
                offerSource={source}
                offerStatus={offerStatus}
                onSelectMembers={queries.memberTable.onSelectMembers}
                selectedAffiliateIds={queries.memberTable.selectedAffiliateIds}
                failedPromoCodeErrors={queries.failedPromoCodeErrors}
                showStoreLevelAffiliateStats={queries.multipleShopifyAffiliateStats.visible}
                connectedStores={queries.multipleShopifyOfferStats.map((s) => ({
                  storeName: s.storeName,
                  clientShopifyConnectionId: s.clientShopifyConnectionId,
                })) || []}
                affiliatesStats={queries.multipleShopifyAffiliateStats.affiliatesStats}
                shopifyStoreName={queries.shopifyStoreName}
                onComplete={queries.refresh}
                isMultipleShopifyOffer={isMultipleShopifyOffer}
                selectedPayoutIds={selectedPayoutIds}
              />
              <MembersWizard
                offerSource={source}
                mode={queries.updateMembers.mode}
                offer={queries.offerDetails}
                onClose={queries.updateMembers.onClose}
                onComplete={queries.refresh}
                selectedMembers={queries.updateMembers.selectedMembers}
                refreshDatesOnly={queries.updateMembers.refreshDatesOnly}
                visible={queries.updateMembers.visible}
                failedPromoCodeErrors={queries.failedPromoCodeErrors}
              />
              {includeFailedMembersWizard && (
                <MembersWizard
                  offerSource={source}
                  mode="refresh"
                  offer={queries.offerDetails}
                  onClose={queries.failedMembers.dismissFailedMembers}
                  onComplete={queries.refresh}
                  refreshDatesOnly={false}
                  selectedMembers={queries.failedMembers.members}
                  visible={queries.failedMembers.visible}
                  failedPromoCodeErrors={queries.failedPromoCodeErrors}
                />
              )}
              <RefreshManageOffer
                onComplete={queries.refresh}
                offer={queries.offerDetails}
                visible={queries.updateManageOffer.visible}
                offerSource={source}
                selectedMembers={queries.updateMembers.selectedMembers}
                onClose={queries.updateManageOffer.onClose}
                manageDeepLinksAllowed={isManageDeepLinksAllowed}
              />
              <MemberConversionDrawer
                offer={queries.offerDetails}
                source={source}
                isOpen={queries.memberConversion.visible}
                selectedMemberConversion={queries.memberConversion.selectedMemberConversion}
                onClose={queries.memberConversion.onClose}
                refresh={queries.refresh}
                dateRangeSettings={dateRangeSettings}
              />

              <MigrationModelContent
                baseUri={baseUri}
                showMigrationModal={showMigrationModal}
                setShowMigrationModal={setShowMigrationModal}
                offer={queries.offerDetails}
                source={source}
                profile={profile}
                onComplete={queries.refresh}
              />

              <OfferLogDrawer
                offerId={queries.offerDetails.id}
                onClose={() => setIsOfferLogDrawerVisible(false)}
                open={isOfferLogDrawerVisible}
                source={source}
              />
              {!isEmpty(includedMemberIds) && (
                <Modal
                  title="Notify members"
                  open={isNotifyModalOpen}
                  onOk={closeNotifyModal}
                  onCancel={closeNotifyModal}
                  cancelText="Don’t Notify"
                  okText={emailUpdateComposeButton}
                  okType="primary"
                >
                  <Text>Do you want to send an email to notify all the members in this offer of the changes?</Text>
                  <br />
                  <Text>Changes will be directly reflected in the creator portal.</Text>
                </Modal>
              )}
            </div>
          </>
        );
    }
  };

  return <OfferDetails>{getOfferDetailsPageContents()}</OfferDetails>;
};
